import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bug, PlayCircle } from "lucide-react";
import FrothAiIcon from "../common/FrothAiIcon";
import Header from "../Header/Header";
import Footer from "../Footer/footer";
import SEOHead from "../SEO/SEOHead";
import WhyFrothTestops from "./WhyFrothTestops";
import WhyHero from "./WhyHero";
import WhyFit from "./WhyFit";
import "../../css/MainPage.css";
import "../../css/Pages.css";
import "../../css/WhyTestops.css";
import LifecycleMap from "./LifecycleMap";
import WhyReasons from "./WhyReasons";
import dashboardShot from "../../../assets/product/project-dashboard.webp";
import automationShot from "../../../assets/product/automation-script-ai.webp";
import securityShot from "../../../assets/product/security-dashboard.webp";

const values = [
  {
    tone: "blue",
    tag: "Test management",
    shot: dashboardShot,
    size: [1800, 562],
    chip: "One source of truth",
    alt: "Froth TestOps project dashboard with requirements, test cases, defects and test plans",
    title: "Unified test management",
    text: "Requirements, tests, executions, defects, tasks and reports live together, so everyone works from one source of truth, with FrothAI to help at every step.",
    link: { to: "/workflow", label: "See the workflow" },
  },
  {
    tone: "violet",
    tag: "FrothAI automation",
    shot: automationShot,
    size: [1800, 500],
    chip: "Playwright & Appium",
    alt: "Creating an automation script in Froth TestOps, recorded manually or generated with FrothAI",
    ai: true,
    title: "Intelligent Automation, Less Code",
    text: "Record scripts with the TestOps Recorder or let FrothAI generate Playwright and Appium scripts from your test cases, then run them on your own CI runners and BrowserStack devices.",
    link: { to: "/froth-ai", label: "Explore FrothAI" },
  },
  {
    tone: "teal",
    tag: "Test Labs",
    shot: securityShot,
    size: [1800, 662],
    chip: "API · Performance · Security · Code",
    alt: "Security Test Lab dashboard with alert levels and recent scan insights",
    title: "One Platform Instead of Many Tools",
    text: "Test management, automation, API, performance, security and code-scanning labs in one platform, so there are fewer tools to buy, connect and maintain.",
    link: { to: "/test-labs", label: "Explore Test Labs" },
  },
];

// Figures supplied by the business; update only from a verified source.
const stats = [
  { value: 17000, suffix: "+", label: "AI-generated test cases", icon: FrothAiIcon, tone: "sky", link: { to: "/froth-ai", label: "See how FrothAI helps" } },
  { value: 480, suffix: "+", label: "New defects created per day", icon: Bug, tone: "violet", link: { to: "/workflow", label: "See defect management" } },
  { value: 33000, suffix: "+", label: "Test cases executed monthly", icon: PlayCircle, tone: "sun", link: { to: "/byteam", label: "See test execution" } },
];

// Counts a figure up from zero the first time it scrolls into view.
function CountUp({ value }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(value);

  useEffect(() => {
    const el = ref.current;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!el || reduce || typeof IntersectionObserver === "undefined") return undefined;
    setShown(0);
    let frame;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const tick = now => {
          const t = Math.min(1, (now - start) / 1400);
          setShown(Math.round(value * (1 - Math.pow(1 - t, 3))));
          if (t < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [value]);

  return <span ref={ref}>{shown.toLocaleString("en-US")}</span>;
}

const WhyTestops = () => {
  return (
    <>
      <SEOHead
        title="Top AI Software Testing Tools to Boost QA Efficiency"
        description="Discover top AI software testing tools to streamline QA workflows, enhance team collaboration, boost test efficiency, and deliver projects faster."
        keywords="AI software testing tools, test management platform, Froth TestOps"
      />
      <div className="fd-main-wrapper page-padding why-page">
        <Header />
        <main>
          <WhyHero />

          <section className="ft-section">
            <div className="ft-container">
              <WhyReasons reasons={values} />
            </div>
          </section>

          <section className="ft-section ft-section--alt">
            <div className="ft-container">
              <div className="why-lifecycle-head">
                <span className="ft-eyebrow">End to end</span>
                <h2 className="ft-heading">
                  Oversee every stage, <span>from plan to release</span>
                </h2>
                <p className="why-lifecycle-head__sub">
                  Twelve modules cover the whole QA lifecycle in one platform, with{" "}
                  <strong className="why-lifecycle-head__ai">
                    <FrothAiIcon size={15} />
                    FrothAI
                  </strong>{" "}
                  helping at every stage.
                </p>
              </div>
              <LifecycleMap />
            </div>
          </section>

          <section className="ft-section">
            <div className="ft-container">
              <WhyFit />
            </div>
          </section>

          <section className="ft-section why-stats" aria-labelledby="why-stats-title">
            <div className="ft-container">
              <div className="why-stats__head">
                <div>
                  <h2 id="why-stats-title" className="why-stats__title">
                    Teams plan, test and release at scale with FrothAI
                  </h2>
                  <p className="why-stats__lead">
                    Discover how teams on Froth TestOps ship faster, with every test case, run and defect in one place.
                  </p>
                </div>
                <Link to="/contact" className="why-stats__cta">
                  Talk to us <ArrowRight size={18} aria-hidden="true" />
                </Link>
              </div>
              <div className="why-stats__grid">
                {stats.map(({ value, suffix, label, icon: Icon, tone, link }) => (
                  <Link to={link.to} className={`why-stat why-stat--${tone}`} key={label}>
                    <Icon size={30} className="why-stat__icon" aria-hidden="true" />
                    <span className="why-stat__value">
                      <CountUp value={value} />
                      <small>{suffix}</small>
                    </span>
                    <span className="why-stat__label">{label}</span>
                    <span className="why-stat__link">
                      {link.label} <ArrowRight size={15} aria-hidden="true" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <WhyFrothTestops />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default WhyTestops;
